import {
    DateAPI,
    GetPatientDataBaselineResponsePayload,
    GetPatientDataCurrentResponsePayload,
    isPassiveValueType,
    MathAPI,
    Value,
    ValueType,
    ValueTypeValueConverters,
    ValueTypeValueRequiresTimeDisplay
} from "pokal_api"
import { valueTypeNames } from "../../../util/valueTypeNames"
import { AreaChart, EventProps } from "@tremor/react"
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks"
import {
    selectDashboardBaselinePatientData,
    selectDashboardCurrentPatientData,
    selectDashboardSelectedValueType,
    selectDataPoint,
    selectMoodEntry,
    selectQuestionnaire
} from "../../../redux/ProfessionalDashboard"
import { useEffect, useState } from "react"
import notNull from "../../../util/notNull"
import {
    mockPatientUsed,
    mockPhqValueTypeUsed,
    mockSleepValueTypeUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../../../redux/Onboarding"
import {
    MOCK_PHQ_BASELINE_DATA,
    MOCK_PHQ_CURRENT_DATA,
    MOCK_SLEEP_BASELINE_DATA,
    MOCK_SLEEP_CURRENT_DATA
} from "../../../onboarding/mock/mockPatientData"

interface ActiveDataViewProps {
    dataType: "current" | "baseline"
}

function SimpleDataView(props: ActiveDataViewProps) {
    const step = useAppSelector(selectOnboardingStep)
    const running = useAppSelector(selectOnboardingIsRunningState)

    const fetchedSelectedValueType = useAppSelector(
        selectDashboardSelectedValueType
    )
    const fetchedCurrentData = useAppSelector(selectDashboardCurrentPatientData)
    const fetchedBaselineData = useAppSelector(
        selectDashboardBaselinePatientData
    )

    const [selectedValueType, setSelectedValueType] =
        useState<ValueType | null>(null)
    const [currentData, setCurrentData] =
        useState<GetPatientDataCurrentResponsePayload | null>(null)
    const [baselineData, setBaslineData] =
        useState<GetPatientDataBaselineResponsePayload | null>(null)

    useEffect(() => {
        if (mockPatientUsed(running, step)) {
            if (mockPhqValueTypeUsed(running, step)) {
                setSelectedValueType(ValueType.PHQUESTIONNAIRE)
                setCurrentData(MOCK_PHQ_CURRENT_DATA)
                setBaslineData(MOCK_PHQ_BASELINE_DATA)
            } else if (mockSleepValueTypeUsed(running, step)) {
                setSelectedValueType(ValueType.SLEEP_DURATION)
                setCurrentData(MOCK_SLEEP_CURRENT_DATA)
                setBaslineData(MOCK_SLEEP_BASELINE_DATA)
            }
        } else {
            setSelectedValueType(fetchedSelectedValueType)
            setCurrentData(fetchedCurrentData)
            setBaslineData(fetchedBaselineData)
        }
    }, [
        fetchedBaselineData,
        fetchedCurrentData,
        fetchedSelectedValueType,
        step,
        running
    ])

    /* eslint-disable  @typescript-eslint/no-explicit-any */ //This is required here because of the charts.
    const [displayedData, setDisplayedData] = useState<{
        data: any[]
        names: string[]
        hasNotes: boolean
    }>({
        data: [],
        names: [],
        hasNotes: false
    })

    function transformData(data: Value[], valueType: ValueType) {
        const sorted = [...data].sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime())
        const name = valueTypeNames[valueType]
        // Only passive metrics open the generic DataPointNotesPanel on click (see
        // showQuestionnaireDetails below) — only mark those, so the dot always points
        // somewhere a note can actually be seen.
        const markNotes = isPassiveValueType(valueType)
        return sorted.map((d) => {
            const baseDate = DateAPI.timestampToString(
                new Date(d.date).getTime(),
                valueType === ValueType.MOOD // Stimmungserfassung: mit Uhrzeit, sonst nur Datum
            )
            const hasNote = markNotes && !!(d.note && d.note.trim())
            return {
                date: hasNote ? baseDate + " •" : baseDate,
                [name]: d.value,
                id: d.id
            }
        })
    }

    useEffect(() => {
        if (notNull(selectedValueType)) {
            let parsed: Value[] = []
            if (props.dataType === "current" && notNull(currentData))
                parsed = currentData
            if (props.dataType === "baseline" && notNull(baselineData))
                parsed = baselineData
            setDisplayedData({
                data: transformData(parsed, selectedValueType),
                names: [valueTypeNames[selectedValueType]],
                hasNotes:
                    isPassiveValueType(selectedValueType) &&
                    parsed.some((d) => d.note && d.note.trim())
            })
        }
    }, [props.dataType === "current" ? currentData : baselineData])

    const dispatch = useAppDispatch()
    function showQuestionnaireDetails(v: EventProps) {
        if (v === null) {
            dispatch(selectQuestionnaire({ type: null, id: null }))
            dispatch(selectMoodEntry(null))
            dispatch(selectDataPoint(null))
        } else if (
            notNull(selectedValueType) &&
            (selectedValueType === ValueType.PHQUESTIONNAIRE ||
                selectedValueType === ValueType.SFQUESTIONNAIRE) &&
            v !== undefined &&
            v.id
        ) {
            dispatch(
                selectQuestionnaire({
                    type: selectedValueType,
                    id: String(v.id)
                })
            )
        } else if (
            selectedValueType === ValueType.MOOD &&
            v !== undefined &&
            v.id
        ) {
            const raw: Value[] =
                props.dataType === "current"
                    ? (currentData ?? [])
                    : (baselineData ?? [])
            const entry = raw.find((d) => d.id === String(v.id)) ?? null
            dispatch(selectMoodEntry(entry))
        } else if (notNull(selectedValueType) && v !== undefined && v.id) {
            // Passive metrics (e.g. steps) rendered as a line chart.
            const raw: Value[] =
                props.dataType === "current"
                    ? (currentData ?? [])
                    : (baselineData ?? [])
            const entry = raw.find((d) => d.id === String(v.id)) ?? null
            dispatch(selectDataPoint(entry))
        }
    }

    // Y-Achsen-Props für SFQ
    const isSFQ = selectedValueType === ValueType.SFQUESTIONNAIRE;
    const yAxisLabel = isSFQ ? 'Beeinträchtigung' : undefined;
    const showYAxis = true;
    // Für SFQ: valueFormatter gibt leeren String zurück, damit keine Ticks angezeigt werden
    const yValueFormatter = isSFQ
        ? (() => '')
        : ((val: number) =>
            selectedValueType !== null
                ? ValueTypeValueConverters[selectedValueType](MathAPI.roundToOneDecimal(val))
                : ''
        );

    return notNull(selectedValueType) ? (
        <>
            {displayedData.hasNotes ? (
                <p className="text-xs text-gray-500 mb-1">
                    • kennzeichnet Tage mit Notiz
                </p>
            ) : null}
            <AreaChart
                data={displayedData.data}
                categories={displayedData.names}
                index={"date"}
                valueFormatter={yValueFormatter}
                onValueChange={(v) => showQuestionnaireDetails(v)}
                yAxisLabel={yAxisLabel}
                showYAxis={showYAxis}
            />
        </>
    ) : (
        <></>
    )
}

export default SimpleDataView
